import { AxiosError } from 'axios';
import io from 'socket.io-client';
import { intermindConfig } from '../../../config';
import intermind, { UserInfo } from '../../../index';
import {
  CreateParticipantParams,
  JoinRequestRejectedException,
  Participant,
  ParticipantJoinRequest,
  UpdateParticipantParams,
} from '../../../types';

export class ParticipantResource {
  async create(params: CreateParticipantParams): Promise<Participant> {
    const { data } = await intermind.httpClient.post<Participant>(`/participants`, params);
    return data;
  }

  async createAnonymous(params: CreateParticipantParams): Promise<Participant> {
    const { data } = await intermind.httpClient.post<Participant | ParticipantJoinRequest>(`/participants/anonymous`, {
      language: params.language,
    });

    if (!data) {
      throw new Error('Failed to create participant');
    }

    if (data._ === 'Participant') {
      return data as Participant;
    } else if (data._ === 'ParticipantJoinRequest') {
      const { roomId } = params;
      return this.waitForJoinApproval(roomId, data.id);
    } else {
      throw new Error('Failed to create participant');
    }
  }

  private async waitForJoinApproval(roomId: string, joinRequestId: string): Promise<Participant> {
    const token = await intermindConfig.get('apiToken')();
    return new Promise((resolve, reject) => {
      console.log('🔄 join  intermindConfig:', intermindConfig.get('wsPath') + '/conference');

      const socket = io('/', {
        path: intermindConfig.get('wsPath') + '/conference',
        transports: ['websocket'],
        upgrade: false,
        query: { roomId, joinRequestId },
        auth: { token: token },
      });

      socket.on('connect_error', (error) => reject(error));

      socket.on('join_request.approved', (participant: Participant) => {
        socket.disconnect();
        resolve(participant);
      });

      socket.on('join_request.rejected', () => {
        socket.disconnect();
        throw new JoinRequestRejectedException();
      });
    });
  }

  async getUser(participantId: string): Promise<UserInfo | null> {
    try {
      const { data } = await intermind.httpClient.get<UserInfo>(`/participants/${participantId}/user`);
      return data;
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response?.status === 404) return null;
      throw error;
    }
  }

  async getUsers(participantIds: string[]): Promise<Record<string, UserInfo | null>> {
    if (!participantIds.length) return {};

    const params = new URLSearchParams();
    participantIds.forEach((id) => params.append('ids', id));

    try {
      const { data } = await intermind.httpClient.get<Record<string, UserInfo | null>>(
        `/participants/users?${params.toString()}`
      );
      return data;
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response?.status === 404) return {};
      throw error;
    }
  }

  async update(params: UpdateParticipantParams): Promise<void> {
    await intermind.httpClient.patch(`/participants/${params.participantId}`, params);
  }

  async updateAnonymous(params: UpdateParticipantParams): Promise<void> {
    await intermind.httpClient.patch(`/participants/anonymous/${params.participantId}`, params);
  }

  async expel(participantId: string): Promise<void> {
    await intermind.httpClient.post(`/participants/${participantId}/expel`);
  }

  async mute(participantId: string): Promise<void> {
    await intermind.httpClient.post(`/participants/${participantId}/mute`);
  }
}
